import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  Calendar,
  GraduationCap,
  Building2,
  Users,
  CheckSquare,
  FileText
} from "lucide-react";

const ChalkupHistory = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Chalkup History - The Classroom Collaboration Tool Microsoft Acquired",
    "description": "The complete history of Chalkup, the classroom collaboration platform that reached 1 in 3 US universities before being acquired by Microsoft in 2018.",
    "author": {
      "@type": "Organization",
      "name": "Chill Study Work"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    },
    "datePublished": "2024-01-01",
    "dateModified": "2024-01-01",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://chillstudy.work/chalkup/history/"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Chalkup History - The Classroom Tool Microsoft Acquired | CSW</title>
        <meta name="description" content="The complete history of Chalkup, the classroom collaboration platform that reached 1 in 3 US universities before being acquired by Microsoft in 2018." />
        <meta name="keywords" content="Chalkup history, Chalkup Microsoft acquisition, classroom collaboration history, Justin Chando, Microsoft Teams Education" />
        <link rel="canonical" href="https://chillstudy.work/chalkup/history/" />
        <meta property="og:title" content="Chalkup History - The Classroom Tool Microsoft Acquired" />
        <meta property="og:description" content="The complete history of Chalkup, from startup to Microsoft acquisition." />
        <meta property="og:url" content="https://chillstudy.work/chalkup/history/" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <Link to="/chalkup/" className="inline-flex items-center gap-2 text-primary hover:underline mb-6">
              ← Back to Chalkup Alternative
            </Link>
            <div className="inline-flex items-center gap-2 bg-orange-500/10 text-orange-500 px-4 py-2 rounded-full text-sm font-medium mb-6 ml-4">
              <GraduationCap className="w-4 h-4" />
              2013 - 2018
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              The Rise and Acquisition of Chalkup
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              How a small edtech startup became the go-to classroom collaboration tool for millions 
              of students, and why Microsoft wanted its technology.
            </p>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {/* 2013: Founding */}
              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-green-500/10 text-green-500">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-green-500">2013</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">The Beginning</h2>
                <p className="text-muted-foreground mb-4">
                  Chalkup was founded in 2013 by <strong>Justin Chando</strong> (CEO) and Tim Costa (CTO). 
                  Chando, a former student frustrated with clunky traditional LMS tools, envisioned Chalkup 
                  as a more social, easy-to-use hub for classwork. The platform launched to let teachers 
                  distribute assignments and resources digitally while letting students collaborate and 
                  discuss coursework in one place.
                </p>
                <p className="text-muted-foreground">
                  In many ways, Chalkup was like a blend of Google Classroom and Edmodo, with its own 
                  spin focused on making classroom interaction feel more natural and engaging.
                </p>
              </div>

              {/* Growth Phase */}
              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-blue-500/10 text-blue-500">
                    <Users className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-blue-500">2014-2017</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">Rapid Adoption in Education</h2>
                <p className="text-muted-foreground mb-4">
                  Chalkup gained significant traction in high schools and colleges across the U.S., 
                  reportedly reaching users in <strong>1 in 5 high schools and 1 in 3 universities</strong> at its peak – 
                  an impressive reach for an edtech startup.
                </p>
                <div className="bg-muted rounded-lg p-6 mb-4">
                  <h3 className="font-semibold mb-3">Key Features That Made Chalkup Popular:</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span><strong>Rubric-based grading:</strong> Teachers could create custom grading rubrics and grade assignments with structured feedback</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span><strong>Discussion boards:</strong> Each class had a space where students could ask questions and comment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span><strong>Google Drive integration:</strong> Seamless file management for classes already using Google Docs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckSquare className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span><strong>Assignment submission:</strong> Students could turn in work online and receive feedback</span>
                    </li>
                  </ul>
                </div>
                <p className="text-muted-foreground">
                  The platform was often described as more intuitive than older systems like Blackboard. 
                  It gave classes a social-media-like space where shy students might feel more comfortable 
                  posting questions online than raising a hand in class.
                </p>
              </div>

              {/* Microsoft Competition */}
              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-yellow-500/10 text-yellow-500">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-yellow-500">2017</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">The Microsoft Challenge</h2>
                <p className="text-muted-foreground mb-4">
                  In 2017, Microsoft launched Microsoft Teams (Education edition), integrated with Office 365. 
                  This posed a serious threat to smaller players like Chalkup. Teams was bundled into schools' 
                  existing Office subscriptions, making it essentially free for institutions already paying for Microsoft services.
                </p>
                <p className="text-muted-foreground">
                  Suddenly, Chalkup found itself competing with a free product from one of the world's largest 
                  tech companies. Rather than fight a losing battle, Chalkup's founder chose to explore other options.
                </p>
              </div>

              {/* Acquisition */}
              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-purple-500/10 text-purple-500">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-purple-500">February 2018</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">The Microsoft Acquisition</h2>
                <p className="text-muted-foreground mb-4">
                  In February 2018, Microsoft announced it had <strong>acqui-hired</strong> Chalkup: they hired 
                  Justin Chando and obtained an exclusive license to Chalkup's technology. Effectively, 
                  Chalkup's team and tech were folded into Microsoft to bolster Microsoft Teams for Education.
                </p>
                <p className="text-muted-foreground mb-4">
                  Chando noted that Microsoft's vision for classroom collaboration aligned with Chalkup's and 
                  that by joining Microsoft he could "reach more classrooms at scale." Microsoft explicitly 
                  stated they would carry over Chalkup's beloved rubric grading feature into Teams.
                </p>
                <p className="text-muted-foreground">
                  Chalkup announced it would <strong>cease operations on June 30, 2018</strong> and advised all 
                  users to transition off the platform by that date.
                </p>
              </div>

              {/* Legacy */}
              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-red-500/10 text-red-500">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-red-500">June 30, 2018</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">Shutdown and Legacy</h2>
                <p className="text-muted-foreground mb-4">
                  Chalkup's shutdown was met with mixed feelings in the education tech community. Teachers 
                  and students who loved Chalkup were sad to see it go; it had become a central tool in some 
                  classrooms. The company provided export tools and pointed users to Microsoft Teams.
                </p>
                <p className="text-muted-foreground mb-4">
                  Microsoft Teams did indeed incorporate Chalkup's rubric grading system and the philosophy 
                  that classes need to "discuss and work together" beyond just handing in homework. Justin Chando 
                  became a product manager at Microsoft Education.
                </p>
                <p className="text-muted-foreground">
                  Chalkup serves as a case study of how the edtech startup world often works: good ideas are 
                  frequently acquired and integrated into bigger ecosystems rather than surviving independently. 
                  For the teachers who used it, Chalkup is remembered fondly as a tool that genuinely made their 
                  classrooms more connected.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* External References */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">References & Further Reading</h2>
            <div className="space-y-3">
              <a 
                href="https://www.edsurge.com/news/2018-02-13-chalkup-a-class-collaboration-tool-will-close-shop-and-become-part-of-microsoft-teams" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors"
              >
                <FileText className="w-5 h-5 text-primary" />
                <span>EdSurge: Chalkup Will Close Shop and Become Part of Microsoft Teams</span>
                <ArrowRight className="w-4 h-4 ml-auto" />
              </a>
              <a 
                href="https://techcrunch.com/2018/02/13/microsoft-acquires-classroom-collaboration-startup-chalkup-to-expand-microsoft-teams/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors"
              >
                <FileText className="w-5 h-5 text-primary" />
                <span>TechCrunch: Microsoft Acquires Classroom Startup Chalkup</span>
                <ArrowRight className="w-4 h-4 ml-auto" />
              </a>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Continue the Collaborative Learning Spirit</h2>
            <p className="text-xl text-muted-foreground mb-8">
              While Chalkup's classroom tools live on in Microsoft Teams, you can still experience 
              collaborative learning with CSW's free study rooms and productivity tools.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8">
                <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                  Try CSW Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8">
                <Link to="/alternatives/">
                  View All Alternatives
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ChalkupHistory;
